import { createModel } from "hox";
import React, { useState } from "react";
import { IOrganRemove, IOrganSearch, IOrganUpdate, IOrganInsert, IOrganDetail, IOrganParams, IOrganDisable } from "./orgTypes"
import { TablePaginationConfig } from "antd/es/table/interface";
import { DEFAULT_FIRST_PAGE_NUM, DEFAULT_PAGE_SIZE, defaultMdCmParam } from "../../../utils/constants";
import { IPgProps, ISearchPage } from "../../../types/AppTypes";
import { organService } from "./orgService"
import { globalPrompt } from '@/components/message'
import { filterArrId, transPgToPagination } from "../../../utils/utils"
import useStorageState from "@/hooks/useStorageState";
import { DictCodeEnmu } from "@/types/AppDictEnums";
import { ITreeSelectData } from "@/views/base/dict/dictModel";

type dictObj = {
    [key: string]: any
}

function listChangeTree(list: IOrganDetail[]) {
    let menuObj: dictObj = {}
    list.forEach(item => {
        item.children = []
        menuObj[item.code as string] = item
    })
    return list.filter(item => {
        if (item.pcode !== null && item.pcode !== "") {
            menuObj[item.pcode as string]?.children.push(item)
            return false
        }
        return true
    })
}

// 根据key在树中查找节点
function findNodeByKey(treeData: IOrganDetail[], key: React.Key): IOrganDetail | null {
    for (let node of treeData) {
        if (node.key === key) {
            return node;
        }
        if (node.children) {
            const found = findNodeByKey(node.children, key);
            if (found) {
                return found;
            }
        }
    }
    return null;
}

export const useOrgModel = createModel(function () {
    const [treeSource, setTreeSource, clearTreeSource] = useStorageState<any[]>("orgTree", []);  //机构树数据源
    const initPage: TablePaginationConfig = { pageSize: DEFAULT_PAGE_SIZE, current: DEFAULT_FIRST_PAGE_NUM }
    const initSearchParams: IOrganParams = {}
    const initOrganList: IOrganDetail[] = []
    const [selectedNodes, setSelectedNodes] = useState<any[]>([]);   //树选中的行信息

    const [searchParam, setSearchParam] = useState<IOrganParams>(initSearchParams)
    const [treeOldSource, setTreeOldSource] = useState([])
    const [organTreeRowKey, setOrganTreeRowKey] = useState<React.Key[]>([])
    const [organTreeRow, setOrganTreeRow] = useState<IOrganDetail>({})
    const [organTreePRow, setOrganTreePRow] = useState<IOrganDetail>({})
    const [addTreeFrom, setAddTreeFrom] = useState<IOrganInsert>({});
    const [page, setPage] = useState<TablePaginationConfig>(initPage);
    const [optType, setOptType] = useState<number>(1)
    const [root, setRoot] = useState<boolean>(false)

    const upDataSearchParam = (upDataParam: IOrganParams) => {
        setSearchParam({ ...searchParam, ...upDataParam })
    }

    const resetSearchParam = () => {
        setSearchParam(initSearchParams)
        const resetPage = { pageSize: 15, pageNum: 1 }
        loadData(resetPage, initSearchParams)
    }

    const loadData = async (pages?: ISearchPage, data?: IOrganParams) => {
        const searchParams = data ?? searchParam
        const parsms = {
            ...searchParams,
            page: {
                pageSize: 10000,
                pageNum: 1
            }
        }
        const { retData } = await organService.page(parsms)
        const { records, ...pg } = retData
        setPage(transPgToPagination(pg));
        const newTreeSource = listChangeTree(records) as never[];
        setTreeSource(newTreeSource);

        // 如果有选中的节点，更新 organTreeRow 为最新的数据
        if (organTreeRowKey.length > 0) {
            const currentNode = findNodeByKey(newTreeSource, organTreeRowKey[0]);
            if (currentNode) {
                setOrganTreeRow(currentNode);
            }
        }

        return newTreeSource;
    }

    const loadOldData = async (pages?: ISearchPage, data?: IOrganParams) => {
        let parms = {
            sourceApp: "main",
            page: {
                pageSize: 10000000,
                pageNum: 1,
            },
        }
        let { retData } = await organService.oldPage(parms);
        let { records } = retData;
        setTreeOldSource(listChangeTree(records) as never[]);
        return true;
    }

    const changeTreeForm = (value: any) => {
        console.log(value)
        setOrganTreeRow({ ...organTreeRow, ...value })
    }

    const insertTreeForm = (value: any) => {
        console.log(value)
        setAddTreeFrom({ ...addTreeFrom, ...value })
    }

    const updataTreeForm = async () => {
        const params = {
            ...organTreeRow,
            enable: organTreeRow.enable ? 1 : 0,
            sourceApp: "mdm",
        }
        const { retData, retMsg } = await organService.update(params)
        await globalPrompt("message", { type: "success", text: retMsg });
        // 修改成功后重新加载数据，organTreeRow 会自动更新
        await loadData();
    }

    const addPeerTreeForm = async () => {
        const params = {
            ...addTreeFrom,
            enable: addTreeFrom.enable ? 1 : 0,
            sourceApp: "mdm",
        }
        const { retData, retMsg } = await organService.insert(params)
        await globalPrompt("message", { type: "success", text: retMsg });
        // 新增成功后重新加载数据
        await loadData();
        return retData;
    }

    const removeTreeForm = async () => {
        const params = {
            ...organTreeRow,
            enable: organTreeRow.enable ? 1 : 0,
            sourceApp: "mdm",
        }
        const { retData, retMsg } = await organService.remove(params)
        await globalPrompt("message", { type: "success", text: "删除成功" });
        await loadData()
    }

    const mapTree = (org: any): ITreeSelectData => {
        const haveChildren = Array.isArray(org?.children) && org?.children.length > 0;
        return {
            title: org?.name ?? "",
            value: org?.code ?? "",
            children: haveChildren ? org.children.map(i => mapTree(i)) : []
        };
    }

    const getOrgTreeSelect = (code: DictCodeEnmu.ORG): ITreeSelectData[] => {
        return [mapTree(treeSource[0])]
    }

    return {
        searchParam,
        treeSource,
        organTreeRowKey,
        organTreeRow,
        organTreePRow,
        page,
        optType,
        root,
        addTreeFrom,
        selectedNodes,
        treeOldSource,
        /***method***/
        setTreeOldSource,
        setSelectedNodes,
        getOrgTreeSelect,
        setAddTreeFrom,
        setSearchParam,
        setTreeSource,
        setOrganTreeRowKey,
        setOrganTreeRow,
        setOrganTreePRow,
        setPage,
        setOptType,
        setRoot,
        loadData,
        upDataSearchParam,
        resetSearchParam,
        changeTreeForm,
        insertTreeForm,
        updataTreeForm,
        addPeerTreeForm,
        removeTreeForm,
        loadOldData
    }
});